/**
 * The device key — read from the URL, held in memory, never persisted.
 *
 * Provisioning (SPEC §10) bakes the key into the "Add to Home Screen" URL:
 * `https://dash.example/?k=<device-key>`. iOS keeps that full URL on the icon
 * forever, so the URL *is* the store. There is no key-entry screen the way
 * Android has one, and nothing to type in the field.
 */

/** In-session cache only. See `rememberKey` for why this is not `localStorage`. */
let cached: string | null = null;

/**
 * Pulls the key out of a URL's query string (or its hash, as a fallback).
 *
 * Exported separately from `deviceKey()` so it can be tested against arbitrary
 * URLs without touching `location`.
 */
export function readKeyFromUrl(href: string): string | null {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return null;
  }
  const fromQuery = url.searchParams.get('k');
  if (fromQuery && fromQuery.trim() !== '') return fromQuery.trim();

  // Some share sheets and QR readers hand the URL over with the query moved
  // behind the fragment. Cheap to accept, and the failure it prevents — a
  // provisioned device that silently has no key — costs a trip to the site.
  const hash = url.hash.startsWith('#') ? url.hash.slice(1) : url.hash;
  if (hash !== '') {
    const fromHash = new URLSearchParams(hash).get('k');
    if (fromHash && fromHash.trim() !== '') return fromHash.trim();
  }
  return null;
}

/**
 * The key for this session, or `null` if the WebClip was installed without one.
 *
 * **The URL is the only source of truth** (SPEC §13 Never). Caching it in
 * `localStorage` and reading from there would look like an improvement and would
 * be the opposite: Safari evicts a WebClip's storage on its own schedule, so a
 * device would run for weeks and then quietly stop authenticating with no change
 * anybody made. Worse, a key cached from an old provisioning URL would outlive a
 * re-provisioning and keep reporting under the wrong device.
 */
export function deviceKey(href: string = window.location.href): string | null {
  if (cached !== null) return cached;
  cached = readKeyFromUrl(href);
  return cached;
}

/** Test seam — drops the in-memory cache. */
export function resetKeyCache(): void {
  cached = null;
}

/** `"a1b2…9xyz"` — enough to tell two devices apart, not enough to reuse. */
export function maskKey(key: string): string {
  if (key.length <= 8) return `${key.slice(0, 2)}…`;
  return `${key.slice(0, 4)}…${key.slice(-4)}`;
}
